<script setup lang="ts">
const { color = BRAND_COLOR.primary, size = ELEMENT_SIZE.medium } =
  defineProps<{
    /**
     * The color of the badge. Accepts any known brand color.
     * Defaults to 'primary'.
     * @default BRAND_COLOR.Primary
     */
    color?: BrandColor;
    /**
     * The size of the badge. Setting the size influences the padding and font size.
     * Defaults to 'medium'.
     * @default ELEMENT_SIZE.medium
     */
    size?: ElementSize;
  }>();
</script>

<template>
  <span
    :class="[
      'foundation-badge',
      `foundation-badge--color-${color}`,
      `foundation-badge--size-${size}`,
    ]"
  >
    <slot />
  </span>
</template>

<style scoped lang="scss">
@use "@citizenm/foundation/assets/styles/mixins" as mixins;

.foundation-badge {
  display: inline-block;
  font-weight: var(--cm-foundation-badge-font-weight, 700);
  font-size: var(--cm-foundation-badge-font-size, 1rem);
  line-height: var(--cm-foundation-badge-line-height, 1.4);
  background: var(--cm-foundation-badge-background-color, #ff0038);
  border-radius: var(--cm-foundation-badge-border-radius, 0.25em);

  /* Colors */
  &--color {
    &-black {
      color: var(--cm-color-white);
      background-color: var(--cm-color-black);
    }

    &-primary {
      color: var(--cm-color-white);
      background-color: var(--cm-color-primary);
    }

    &-white {
      color: var(--cm-color-black);
      background-color: var(--cm-color-white);
      border: var(--cm-foundation-badge-border-width, 1px) solid
        var(--cm-color-black, #000);
    }

    &-green {
      color: var(--cm-color-white);
      background-color: var(--color-green, #00a651);
    }

    &-blue {
      color: var(--cm-color-white);
      background-color: var(--color-blue, #0072c6);
    }
  }

  /* Sizes */
  &--size {
    &-small {
      padding: 0.1em 0.4em;
      font-size: var(--cm-font-size-xs, 0.75rem);

      @include mixins.lg {
        font-size: var(--cm-font-size-sm, 0.875rem);
      }
    }

    &-medium {
      padding: 0.2em 0.5em;
      font-size: var(--cm-font-size-sm, 0.875rem);

      @include mixins.lg {
        font-size: var(--cm-font-size-md, 1rem);
      }
    }

    &-large {
      padding: 0.3em 0.6em;
      font-size: var(--cm-font-size-md, 1rem);

      @include mixins.lg {
        font-size: var(--cm-font-size-lg, 1.25rem);
      }
    }
  }
}
</style>
